import React, { useEffect, useId, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/client';
import PageHeader from '../components/common/PageHeader';
import { useNotice } from '../components/common/Notices';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { encryptPassword } from '../utils/crypto';
import { getCurrentUser } from '../utils/storage';

// The app's password policy as one plain sentence ("At least 8 characters, with …").
export const describePolicy = (policy) => {
    if (!policy) return '';
    const parts = [];
    if (policy.require_uppercase) parts.push('an uppercase letter');
    if (policy.require_lowercase) parts.push('a lowercase letter');
    if (policy.require_number) parts.push('a number');
    if (policy.require_special) parts.push('a symbol');
    const min = Number(policy.min_length) > 0 ? `At least ${Number(policy.min_length)} characters` : '';
    const withList =
        parts.length > 1 ? `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}` : parts[0] || '';
    if (min && withList) return `${min}, with ${withList}.`;
    if (min) return `${min}.`;
    if (withList) return `Must include ${withList}.`;
    return '';
};

const Field = ({ label, value }) => (
    <div className="min-w-0">
        <dt className="text-sm text-slate-600">{label}</dt>
        <dd className="mt-0.5 truncate text-sm font-medium text-slate-900" title={value || undefined}>
            {value || <span className="font-normal text-slate-600">Not set</span>}
        </dd>
    </div>
);

// Read from the session token: it already carries these claims, and the details endpoint
// needs an app on the session, which portal-admin console sessions don't have.
const ProfileSection = () => {
    const me = getCurrentUser();
    return (
        <section aria-labelledby="profile-heading" className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
            <h2 id="profile-heading" className="text-base font-semibold text-slate-900">
                Profile
            </h2>
            <p className="mt-1 text-sm text-slate-600">
                Read-only here. Admins can edit these details on the Users page.
            </p>
            <dl className="mt-5 grid gap-4 sm:grid-cols-2">
                <Field label="Name" value={me?.name} />
                <Field label="Username" value={me?.username} />
                <Field label="Email" value={me?.email} />
                <Field label="Phone" value={me?.phone} />
            </dl>
        </section>
    );
};

const PasswordSection = () => {
    const notice = useNotice();
    const ids = useId();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({}); // { old, confirm, new: string[], form }
    const [policyHint, setPolicyHint] = useState('');

    useEffect(() => {
        let cancelled = false;
        api.get('/api/password-policy')
            .then((res) => {
                if (!cancelled) setPolicyHint(describePolicy(res?.data?.data));
            })
            .catch(() => {
                // No hint if the policy can't be read; the server still validates on save.
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        const next = {};
        if (!oldPassword) next.old = 'Enter your current password.';
        if (!newPassword) next.new = ['Enter a new password.'];
        if (newPassword && confirmPassword !== newPassword) next.confirm = 'This doesn’t match the new password.';
        if (newPassword && oldPassword && newPassword === oldPassword) {
            next.new = ['Choose a password that’s different from your current one.'];
        }
        setErrors(next);
        if (Object.keys(next).length) return;

        setSaving(true);
        try {
            await api.post('/api/auth/change-password', {
                oldPassword: encryptPassword(oldPassword),
                newPassword: encryptPassword(newPassword),
                confirmPassword: encryptPassword(confirmPassword),
            });
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            setErrors({});
            notice.success('Password changed. Use the new password next time you sign in.');
        } catch (err) {
            const data = err?.response?.data;
            const message = data?.message || err?.message || '';
            if (Array.isArray(data?.data?.violations) && data.data.violations.length) {
                setErrors({ new: data.data.violations.map((v) => `${v}.`) });
            } else if (/incorrect credentials/i.test(message)) {
                setErrors({ old: 'Your current password is incorrect. Check it and try again.' });
            } else if (/used recently/i.test(message)) {
                setErrors({ new: [message] });
            } else {
                setErrors({ form: message || 'Couldn’t change your password. Try again in a moment.' });
            }
        } finally {
            setSaving(false);
        }
    };

    const fieldId = (k) => `${ids}-${k}`;
    const describedBy = (...list) => list.filter(Boolean).join(' ') || undefined;

    return (
        <section aria-labelledby="password-heading" className="rounded-xl border border-slate-200 bg-white p-5 md:p-6">
            <h2 id="password-heading" className="text-base font-semibold text-slate-900">
                Change password
            </h2>
            <p className="mt-1 text-sm text-slate-600">
                Applies to every app you sign in to with this account. Accounts that sign in through single sign-on
                can’t set a password here.
            </p>

            <form onSubmit={onSubmit} noValidate className="mt-5 max-w-md space-y-4">
                <div>
                    <label htmlFor={fieldId('old')} className="mb-1 block text-sm font-medium text-slate-700">
                        Current password
                    </label>
                    <Input
                        id={fieldId('old')}
                        type="password"
                        autoComplete="current-password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        aria-invalid={errors.old ? true : undefined}
                        aria-describedby={describedBy(errors.old && fieldId('old-err'))}
                        className={errors.old ? 'border-red-300' : undefined}
                    />
                    {errors.old && (
                        <p id={fieldId('old-err')} className="mt-1 text-sm text-red-700">
                            {errors.old}
                        </p>
                    )}
                </div>

                <div>
                    <label htmlFor={fieldId('new')} className="mb-1 block text-sm font-medium text-slate-700">
                        New password
                    </label>
                    <Input
                        id={fieldId('new')}
                        type="password"
                        autoComplete="new-password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        aria-invalid={errors.new ? true : undefined}
                        aria-describedby={describedBy(policyHint && fieldId('hint'), errors.new && fieldId('new-err'))}
                        className={errors.new ? 'border-red-300' : undefined}
                    />
                    {policyHint && (
                        <p id={fieldId('hint')} className="mt-1 text-sm text-slate-600">
                            {policyHint}
                        </p>
                    )}
                    {errors.new && (
                        <ul id={fieldId('new-err')} className="mt-1 space-y-0.5 text-sm text-red-700">
                            {errors.new.map((m) => (
                                <li key={m}>{m}</li>
                            ))}
                        </ul>
                    )}
                </div>

                <div>
                    <label htmlFor={fieldId('confirm')} className="mb-1 block text-sm font-medium text-slate-700">
                        Confirm new password
                    </label>
                    <Input
                        id={fieldId('confirm')}
                        type="password"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        aria-invalid={errors.confirm ? true : undefined}
                        aria-describedby={describedBy(errors.confirm && fieldId('confirm-err'))}
                        className={errors.confirm ? 'border-red-300' : undefined}
                    />
                    {errors.confirm && (
                        <p id={fieldId('confirm-err')} className="mt-1 text-sm text-red-700">
                            {errors.confirm}
                        </p>
                    )}
                </div>

                {errors.form && (
                    <p
                        role="alert"
                        className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm text-red-700"
                    >
                        {errors.form}
                    </p>
                )}

                <Button type="submit" disabled={saving}>
                    {saving ? 'Changing password…' : 'Change password'}
                </Button>
            </form>
        </section>
    );
};

const AccountPage = () => (
    <div className="max-w-3xl space-y-6">
        <PageHeader
            title="Profile & password"
            subtitle={
                <>
                    Your own account. Two-step verification is on the{' '}
                    <Link
                        to="/account/security"
                        className="font-medium text-primary-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-200"
                    >
                        Two-step verification
                    </Link>{' '}
                    page.
                </>
            }
        />
        <ProfileSection />
        <PasswordSection />
    </div>
);

export default AccountPage;
